import React, { useState } from 'react';
import {
    AppBar,
    Toolbar,
    Typography,
    IconButton,
    Box,
    Avatar,
    Menu,
    MenuItem,
    Tooltip,
    Drawer,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Collapse,
    Divider,
    CssBaseline,
} from '@mui/material';

import { Link } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import HomeIcon from '@mui/icons-material/Home';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { createTheme, ThemeProvider } from '@mui/material/styles';

// New Icons for the Finance Module
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People'; // For Customer Accounts
import LocalAtmIcon from '@mui/icons-material/LocalAtm'; // For Loans
import HistoryIcon from '@mui/icons-material/History'; // For Loan History
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet'; // For EMI Taken
import AssignmentIcon from '@mui/icons-material/Assignment'; // For Reports

// Mock user data for the example
const mockUserData = {
    name: 'Shivamba',
    email: '',
};

// -----------------------------------------------------------------------------
// Header Component
// -----------------------------------------------------------------------------
const Financeheader = ({ onMenuClick }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleLogout = () => {
        console.log('User signed out.');
    };

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const userInitial = mockUserData?.name?.charAt(0)?.toUpperCase() || 'U';

    return (
        <AppBar
            position="fixed"
            sx={{
                zIndex: (theme) => theme.zIndex.drawer + 1,
                background: 'linear-gradient(90deg, #b5453fff 0%, #b78d3aff 50%, #b06927ff 100%)',
            }}
        >
            <Toolbar>
                <IconButton edge="start" color="inherit" onClick={onMenuClick} sx={{ mr: 2 }}>
                    <MenuIcon />
                </IconButton>

                <Typography variant="h6" noWrap sx={{ flexGrow: 1 }}>
                    श्रीरामजी
                </Typography>

                <Box>
                    <Tooltip title="Profile">
                        <IconButton onClick={handleMenuOpen} size="small" sx={{ ml: 2 }}>
                            <Avatar sx={{ width: 32, height: 32 }}>
                                {userInitial}
                            </Avatar>
                        </IconButton>
                    </Tooltip>
                    <Menu
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleMenuClose}
                        onClick={handleMenuClose}
                        PaperProps={{
                            elevation: 3,
                            sx: { mt: 1.5 },
                        }}
                    >
                        <MenuItem disabled>
                            <Typography variant="subtitle1">
                                {mockUserData?.name || mockUserData?.email || 'Guest'}
                            </Typography>
                        </MenuItem>
                        <MenuItem onClick={handleLogout}>
                            <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
                            Logout
                        </MenuItem>
                        <MenuItem component={Link} to="/home">
                            Back To Home
                        </MenuItem>
                    </Menu>
                </Box>
            </Toolbar>
        </AppBar>
    );
};

// -----------------------------------------------------------------------------
// Sidebar Component
// -----------------------------------------------------------------------------
const drawerWidth = 240;

const Sidebar = ({ isOpen, onClose }) => {
    const [openReports, setOpenReports] = useState(false);
    const [openLoans, setOpenLoans] = useState(false);

    return (
        <Drawer
            variant="temporary"
            open={isOpen}
            onClose={onClose}
            sx={{
                width: drawerWidth,
                flexShrink: 0,
                '& .MuiDrawer-paper': {
                    width: drawerWidth,
                    boxSizing: 'border-box',
                    background: 'linear-gradient(to bottom, #9a0b0bff, #b45808ff)',
                    color: '#fff',
                    borderRight: 'none',
                },
            }}
            elevation={3}
        >
            <Toolbar />
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)' }} />

            <List component="nav">
                {/* Dashboard */}
                <ListItemButton component={Link} to="/category/finance" sx={styles.menuItem}>
                    <ListItemIcon sx={styles.menuIcon}>
                        <DashboardIcon />
                    </ListItemIcon>
                    <ListItemText primary="Dashboard" />
                </ListItemButton>
                
                {/* Customer Account (Standalone) */}
                <ListItemButton component={Link} to="/finance/customer-account" sx={styles.menuItem}>
                    <ListItemIcon sx={styles.menuIcon}><PeopleIcon /></ListItemIcon>
                    <ListItemText primary="कर्जदार " />
                </ListItemButton>

                {/* Loans (Collapsible) */}
                <ListItemButton onClick={() => setOpenLoans(!openLoans)} sx={styles.menuItem}>
                    <ListItemIcon sx={styles.menuIcon}><LocalAtmIcon /></ListItemIcon>
                    <ListItemText primary="कर्ज " />
                    {openLoans ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
                <Collapse in={openLoans} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                        <ListItemButton component={Link} to="/finance/loans/given" sx={styles.subMenuItem}>
                            <ListItemIcon sx={styles.menuIcon}><HistoryIcon fontSize="small" /></ListItemIcon>
                            <ListItemText primary="कर्ज देणे " />
                        </ListItemButton>
                        <ListItemButton component={Link} to="/finance/loans/emi-taken" sx={styles.subMenuItem}>
                            <ListItemIcon sx={styles.menuIcon}><AccountBalanceWalletIcon fontSize="small" /></ListItemIcon>
                            <ListItemText primary="हप्ता वसुली " />
                        </ListItemButton>
                       
                    </List>
                </Collapse>

                {/* Reports (Collapsible) */}
                <ListItemButton onClick={() => setOpenReports(!openReports)} sx={styles.menuItem}>
                    <ListItemIcon sx={styles.menuIcon}><AssignmentIcon /></ListItemIcon>
                    <ListItemText primary="रिपोर्ट " />
                    {openReports ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
                <Collapse in={openReports} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                        <ListItemButton component={Link} to="/finance/reports/loan-report" sx={styles.subMenuItem}>
                            <ListItemText primary="कर्ज रिपोर्ट " />
                        </ListItemButton>
                        <ListItemButton component={Link} to="/finance/reports/customer-report" sx={styles.subMenuItem}>
                            <ListItemText primary="कर्जदार  रिपोर्ट " />
                        </ListItemButton>
                        <ListItemButton component={Link} to="/finance/reports/emi-report" sx={styles.subMenuItem}>
                            <ListItemText primary="हप्ता वसुली रिपोर्ट " />
                        </ListItemButton>
                    </List>
                </Collapse>
            </List>
        </Drawer>
    );
};

const styles = {
    menuItem: {
        px: 2,
        py: 1.5,
        color: '#fff',
        '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
    },
    menuIcon: { color: '#fff', minWidth: '40px' },
    subMenuItem: {
        pl: 5,
        py: 1,
        color: '#ddd',
        '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
    },
};

// -----------------------------------------------------------------------------
// Main App Component
// -----------------------------------------------------------------------------
const Index = () => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);
    const theme = createTheme();

    return (
        <ThemeProvider theme={theme}>
            <Box sx={{ display: 'flex' }}>
                <CssBaseline />
                <Financeheader onMenuClick={toggleSidebar} />
                <Sidebar isOpen={isSidebarOpen} onClose={toggleSidebar} />
            </Box>
        </ThemeProvider>
    );
};

export default Index;